import { Pin } from "lucide-react";
import { TagBadge } from "@/components/ui/TagBadge";
import { VoteButtons } from "@/components/ui/VoteButtons";
import type { DecisionListItem } from "../types";

type Props = {
  decision: DecisionListItem;
  onClick: () => void;
  onTogglePin: () => void;
  onVote: (type: "up" | "down") => void;
  tagLabels?: Record<string, string>;
  categoryLabels?: Record<string, string>;
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  open: { bg: "hsl(210,85%,95%)", text: "hsl(210,85%,38%)" },
  decided: { bg: "hsl(142,55%,94%)", text: "hsl(142,65%,30%)" },
  implemented: { bg: "hsl(180,60%,93%)", text: "hsl(180,70%,28%)" },
  archived: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
};

const IMPACT_COLORS: Record<string, { bg: string; text: string }> = {
  critical: { bg: "hsl(0,80%,95%)", text: "hsl(0,75%,38%)" },
  high: { bg: "hsl(25,95%,94%)", text: "hsl(25,85%,38%)" },
  medium: { bg: "hsl(40,95%,94%)", text: "hsl(35,85%,38%)" },
  low: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
};

export function DecisionCard({
  decision,
  onClick,
  onTogglePin,
  onVote,
  tagLabels = {},
  categoryLabels = {},
}: Props) {
  const statusColor = STATUS_COLORS[decision.status] ?? STATUS_COLORS.open;
  const impactColor = IMPACT_COLORS[decision.impact] ?? IMPACT_COLORS.medium;
  const visibleTags = decision.tags.slice(0, 3);
  const extraTags = decision.tags.length - visibleTags.length;
  const categoryLabel = decision.category
    ? (categoryLabels[decision.category] ?? decision.category)
    : null;
  const decidedByLabel = decision.decidedByName ?? "—";

  return (
    <div
      onClick={onClick}
      className="group flex cursor-pointer flex-col rounded-xl border bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
      style={{ borderColor: "hsl(210,30%,85%)" }}
    >
      <div className="mb-2 flex items-center justify-between">
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize"
          style={{ background: statusColor.bg, color: statusColor.text }}
        >
          {decision.status}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
          title={decision.isPinned ? "Unpin" : "Pin"}
          className="flex h-6 w-6 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
        >
          <Pin
            className="h-[14px] w-[14px]"
            strokeWidth={1.5}
            style={{
              color: decision.isPinned ? "hsl(40,95%,50%)" : "hsl(215,16%,65%)",
              fill: decision.isPinned ? "hsl(40,95%,55%)" : "none",
            }}
          />
        </button>
      </div>

      <div className="mb-2" onClick={(e) => e.stopPropagation()}>
        <VoteButtons
          votesUp={decision.votesUp}
          votesDown={decision.votesDown}
          userVote={decision.currentUserVote}
          onVote={onVote}
          size="sm"
        />
      </div>

      <div
        className="mb-1.5 text-[14px] font-semibold leading-snug text-foreground"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {decision.title}
      </div>

      <div
        className="mb-3 text-[12px] text-muted-foreground"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {decision.contentPreview.slice(0, 120) || (
          <span className="italic">No content</span>
        )}
      </div>

      {visibleTags.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1">
          {visibleTags.map((t) => (
            <TagBadge key={t} value={t} label={tagLabels[t] ?? t} size="sm" />
          ))}
          {extraTags > 0 && (
            <span className="text-[11px] text-muted-foreground">
              +{extraTags} more
            </span>
          )}
        </div>
      )}

      <div className="mt-auto flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium capitalize"
          style={{ background: impactColor.bg, color: impactColor.text }}
        >
          {decision.impact}
        </span>
        {categoryLabel && (
          <span className="truncate">{categoryLabel}</span>
        )}
        <span className="truncate">
          {decidedByLabel} · {formatDate(decision.decidedAt)}
        </span>
      </div>
    </div>
  );
}
